import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './Button';
import { Icon } from './Icon';
import { SchemaBadge, SchemaForm, validateRequired, type JSONSchema } from './SchemaForm';
import { useAuth } from '@/store/auth';
import { useAttributeDefs } from '@/hooks/useAttributeDefs';
import { declareEvent, newEventId } from '@/api/events';
import { attrTypeLabel } from '@/lib/format';
import type { AttributeDef } from '@/api/attributeDefs';

type Props = {
  opened: boolean;
  onClose: () => void;
  entrepreneurPhone: string;
  entrepreneurName: string;
};

/**
 * Modal that lets an operator declare an event manually for a given
 * entreprenant. The form is generated dynamically from the selected
 * attribute's `payloadSchema`; the source is fixed to MANUAL (or
 * BACK_OFFICE when MANUAL isn't in `allowedSources`) and the actorId is
 * derived from the JWT principal.
 *
 * The mutation invalidates the entrepreneur + timeline caches on
 * success so the parent page picks up the new event without an extra
 * round-trip.
 */
export function DeclareEventModal({ opened, onClose, entrepreneurPhone, entrepreneurName }: Props) {
  const queryClient = useQueryClient();
  const user = useAuth((s) => s.user);
  const defsQuery = useAttributeDefs();

  const manualEligible = useMemo(() => {
    return (defsQuery.data ?? []).filter(
      (d) =>
        d.status === 'ACTIVE' &&
        d.subjectType === 'ENTREPRENEUR' &&
        (d.allowedSources ?? []).some((s) => s === 'MANUAL' || s === 'BACK_OFFICE'),
    );
  }, [defsQuery.data]);

  const [code, setCode] = useState<string>('');
  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const def = manualEligible.find((d) => d.code === code);

  // Pick the first eligible attribute as the initial selection once the
  // dictionary has loaded.
  useEffect(() => {
    if (!code && manualEligible.length > 0) {
      setCode(manualEligible[0].code);
    }
  }, [code, manualEligible]);

  // Reset the payload when the attribute changes so stray fields from
  // the previous schema don't leak into the next request.
  useEffect(() => {
    setPayload({});
    setServerError(null);
  }, [code]);

  const errors = useMemo(() => {
    if (!def) return {};
    return validateRequired(def.payloadSchema as JSONSchema, payload);
  }, [def, payload]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!def) throw new Error('no attribute selected');
      const source = pickSource(def);
      return declareEvent({
        eventId: newEventId(),
        occurredAt: new Date().toISOString(),
        attributeType: def.code,
        phoneNumber: entrepreneurPhone,
        source: {
          system: source,
          actorId: user?.email ?? `account_${user?.accountId ?? ''}`,
          actorName: user?.displayName ?? undefined,
        },
        payload,
      });
    },
    onSuccess: async (result) => {
      if (!result.ok) {
        setServerError(`${result.code}: ${result.message}`);
        return;
      }
      // Invalidate the consolidated view + the timeline so the
      // entrepreneur page reflects the new event immediately.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['entrepreneurs', 'get', entrepreneurPhone] }),
        queryClient.invalidateQueries({ queryKey: ['entrepreneurs', 'timeline', entrepreneurPhone] }),
        queryClient.invalidateQueries({ queryKey: ['entrepreneurs', 'search'] }),
      ]);
      onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof Error ? err.message : 'Erreur inconnue');
    },
  });

  const blockingErrors = Object.keys(errors).length > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size={720}
      padding={0}
      centered
      overlayProps={{ blur: 2 }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '20px 22px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--panel)',
          flex: '0 0 auto',
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--panel)',
            border: '1px solid var(--line-2)',
            color: 'var(--accent-2)',
            boxShadow: 'var(--sh-sm)',
            flex: '0 0 auto',
          }}
        >
          <Icon name="plus" size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 16.5, letterSpacing: '-.02em' }}>
            Déclarer un événement
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 600, marginTop: 2 }}>
            Pour {entrepreneurName} · {entrepreneurPhone}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            border: '1px solid var(--line)',
            background: 'var(--panel)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            color: 'var(--ink-2)',
            boxShadow: 'var(--sh-sm)',
            flex: '0 0 auto',
          }}
        >
          <Icon name="x" size={18} />
        </button>
      </header>

      <div style={{ padding: '22px 24px', overflowY: 'auto' }}>
        <div className="field" style={{ marginBottom: 18 }}>
          <label>Type d'attribut</label>
          <select
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 13px',
              borderRadius: 10,
              border: '1px solid var(--line-2)',
              font: '600 13.5px var(--ff)',
              color: 'var(--ink)',
              background: 'var(--panel)',
              outline: 'none',
            }}
          >
            {manualEligible.map((d) => (
              <option key={d.code} value={d.code}>
                {attrTypeLabel(d.code)} · {d.code}
              </option>
            ))}
          </select>
          {def && (
            <div
              className="hint"
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}
            >
              <SchemaBadge version={def.schemaVersion} />
              <span>Formulaire généré d'après le dictionnaire d'attributs.</span>
            </div>
          )}
        </div>

        {def && (
          <div className="card pad" style={{ background: 'var(--panel-2)' }}>
            <SchemaForm
              schema={def.payloadSchema as JSONSchema}
              value={payload}
              onChange={setPayload}
              errors={errors}
            />
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 14,
            marginTop: 18,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <span className="meta acc">
            <span className="sq" />
            Source : {def ? pickSource(def) : '—'}
          </span>
          <span className="meta acc">
            <span className="sq" />
            Déclaré par : {user?.email ?? '—'}
          </span>
        </div>

        {serverError && (
          <div className="err-msg" style={{ marginTop: 14 }}>
            {serverError}
          </div>
        )}
      </div>

      <footer
        style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end',
          padding: '16px 22px',
          borderTop: '1px solid var(--line)',
          background: 'var(--panel)',
          flex: '0 0 auto',
        }}
      >
        <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>
          Annuler
        </Button>
        <Button
          variant="primary"
          icon="check"
          onClick={() => submit.mutate()}
          loading={submit.isPending}
          disabled={!def || blockingErrors}
        >
          Soumettre l'événement
        </Button>
      </footer>
    </Modal>
  );
}

/**
 * Picks the upstream `source.system` value that satisfies the
 * dictionary's allowedSources. The platform models manual declarations
 * either as MANUAL (operator-initiated, like a back-office note) or
 * BACK_OFFICE (admin-initiated update on the projection); we prefer
 * MANUAL when allowed.
 */
function pickSource(def: AttributeDef): string {
  const allowed = def.allowedSources ?? [];
  if (allowed.includes('MANUAL')) return 'MANUAL';
  if (allowed.includes('BACK_OFFICE')) return 'BACK_OFFICE';
  return allowed[0] ?? 'MANUAL';
}
